// Maps classified MCP error codes (connect-phase MCP_CONNECT_*/EXECUTOR_* and runtime
// MCP_*/NEEDS_AUTH) to localized messages under Mcp.connectErrors.*. Static switch — no
// dynamic i18n keys, so a code the catalog doesn't know can never raise MISSING_MESSAGE;
// unknown codes fall back to the backend diagnostic, then to the generic message.
//
// Code sources:
//  - api-ai-transform model/error_codes.go  (MCP_CONNECT_* …; MCP_FORBIDDEN … runtime)
//  - executor-side contract                 (docs/mcp-connector/checklist.md)
//  - api-ai-transform forward sentinels      (EXECUTOR_* — grouped as serviceUnavailable)

import { MCPConnectError } from "@/services/mcp-service";

// Translator shape for useTranslations("Mcp") — only what this helper calls.
type McpTranslator = (key: string) => string;

// Code-first core: map an error_code (+ optional backend fallback message) to a localized
// message. Used by the connect wrapper below AND direct call sites (discover surfacing).
export function messageForMcpCode(
  code: string | undefined,
  fallback: string | undefined,
  tMcp: McpTranslator,
): string {
  switch (code) {
    // api-ai-transform (raised before the executor forward)
    case "MCP_CONNECT_NOT_FOUND":
      return tMcp("connectErrors.notFound");
    case "MCP_CONNECT_LOOKUP_FAILED":
      return tMcp("connectErrors.lookupFailed");
    case "MCP_CONNECT_NO_CONFIG":
      return tMcp("connectErrors.noConfig");
    case "MCP_CONNECT_NO_AUTH_CONFIG":
      return tMcp("connectErrors.noAuthConfig");
    case "MCP_CONNECT_CLIENT_ID_REQUIRED":
    case "MISSING_OAUTH2_CLIENT_ID": // legacy code kept for older deploys
      return tMcp("connectErrors.clientIdRequired");

    // executor-side contract (token/consent/upstream)
    case "MCP_CONNECT_DISCOVERY_FAILED":
      return tMcp("connectErrors.discoveryFailed");
    case "MCP_CONNECT_DCR_FAILED":
      return tMcp("connectErrors.dcrFailed");
    case "MCP_CONNECT_TOKEN_EXCHANGE_FAILED":
      return tMcp("connectErrors.tokenExchangeFailed");
    case "MCP_CONNECT_INVALID_CREDENTIALS":
      return tMcp("connectErrors.invalidCredentials");
    case "MCP_CONNECT_UPSTREAM_UNREACHABLE":
      return tMcp("connectErrors.upstreamUnreachable");
    case "MCP_CONNECT_UPSTREAM_TIMEOUT":
      return tMcp("connectErrors.upstreamTimeout");
    case "MCP_CONNECT_STATE_MISMATCH":
      return tMcp("connectErrors.stateMismatch");
    case "MCP_CONNECT_CONSENT_DENIED":
      return tMcp("connectErrors.consentDenied");

    // api-ai-transform → executor transport sentinels: one user-facing bucket.
    case "EXECUTOR_UNCONFIGURED":
    case "EXECUTOR_UNREACHABLE":
    case "EXECUTOR_READ_FAILED":
    case "EXECUTOR_RETURNED_NON_JSON":
    case "EXECUTOR_FORWARD_FAILED":
      return tMcp("connectErrors.serviceUnavailable");

    // Stage 1 runtime data-plane codes (discover / tools/call / resources / prompts)
    case "NEEDS_AUTH":
      return tMcp("connectErrors.needsAuth");
    case "MCP_FORBIDDEN":
      return tMcp("connectErrors.forbidden");
    case "MCP_UNAVAILABLE":
      return tMcp("connectErrors.unavailable");
    case "MCP_PROTOCOL":
      return tMcp("connectErrors.protocol");
    case "MCP_SERVER_ERROR":
      // the server's own message IS the content (e.g. "Playlist not found") — show it verbatim.
      return fallback || tMcp("connectErrors.default");

    // Flow B OAuth popup codes (api-ai-transform/api/mcp.go) — AD-4 / step 29.
    // executor_unreachable is lowercase on the wire; switch is case-sensitive so a
    // separate case is needed alongside the uppercase EXECUTOR_UNREACHABLE above.
    case "access_denied":
      return tMcp("connectErrors.accessDenied");
    case "invalid_request":
      return tMcp("connectErrors.invalidRequest");
    case "executor_unreachable":
      return tMcp("connectErrors.executorUnreachable");
    case "bad_executor_response":
      return tMcp("connectErrors.badExecutorResponse");
    case "INVALID_STATE":
      return tMcp("connectErrors.invalidState");
    case "STATE_EXPIRED":
      return tMcp("connectErrors.stateExpired");

    default:
      return fallback || tMcp("connectErrors.default");
  }
}

// Back-compat wrapper — unchanged signature/behavior for existing connect callers.
export function mcpConnectErrorMessage(
  err: unknown,
  tMcp: McpTranslator,
): string {
  if (err instanceof MCPConnectError) {
    return messageForMcpCode(err.code, err.message, tMcp);
  }
  return err instanceof Error && err.message
    ? err.message
    : tMcp("connectErrors.default");
}
